import { useMemo, useEffect, useState, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import {ArrowRight, ArrowLeft, ShieldCheck, Waves, BookOpen, Briefcase, ChevronRight, Layers, GraduationCap, MapPin, Award} from 'lucide-react';
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import { seoPages } from "@/data/seoPages";
import { useSeoPages, refreshSeoPagesIfStale } from "@/hooks/useSeoPages";
import { jobs } from "@/data/jobs";
import { useAllJobs, recruiterJobToJob, type RecruiterJob } from "@/hooks/useRecruiterJobs";
import { fetchCompanyProfilesByCreators } from "@/hooks/useCompanyProfile";
import JobCard from "@/components/JobCard";

// ─── Photo assets per silo ──────────────────────────────────────────────────
const BNSSA_ASSETS = {
  hero: "https://static.lumi.new/ca/ca0184d0e6722dac4690a736d7129164.png",
  action: "https://file.lumi.new/p443756950150270976/SPF2055895787539656704.png",
  profile: "https://static.lumi.new/04/043e92a72b63e01a16fc29df2d5d2a1e.webp",
  aerial: "https://static.lumi.new/ab/abe3216c4827859a0de63f52cbb4e038.png"
};

const MNS_ASSETS = {
  hero: "https://static.lumi.new/fc/fc27d3560136ed60d6abf09476d0e884.png",
  action: "https://static.lumi.new/be/beb6c31f448339034f32de0e15e9cf3c.png",
  profile: "https://static.lumi.new/c0/c08ed4df170d6a1148e3e2c6f4dfaf0f.png",
  pool: "https://static.lumi.new/dd/dd41c3b7df88792132cd2662836ff391.webp"
};

export default function SeoPage() {
  const location = useLocation();
  const { mergedPages, error, hasFetchedSuccessfully } = useSeoPages();
  const { jobs: recruiterJobs } = useAllJobs();

  // Map creator -> { logoUrl } pour enrichir les offres recruteurs avec leur logo
  // sur les pages SEO emploi BNSSA / MNS (cohérent avec /offres et /offre/:id).
  const [logosByCreator, setLogosByCreator] = useState<Record<string, { logoUrl?: string }>>({});

  const normalizedPath = useMemo(() => {
    const p = location.pathname.replace(/\/+$/, "");
    return p || "/";
  }, [location.pathname]);

  // CRITICAL Chrome Android: à chaque navigation client (et au mount après
  // restauration bfcache), on vérifie que les données SEO sont fraîches.
  // Si elles datent de plus de 10s, un refresh est déclenché en arrière-plan.
  useEffect(() => {
    refreshSeoPagesIfStale();
  }, [normalizedPath]);

  // Smooth scroll vers l'ancre avec offset (header sticky ~72 px).
  // Déclenché quand l'utilisateur arrive avec un #hash dans l'URL ou clique
  // sur un bouton de la nav rapide (qui change location.hash).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = location.hash;
    if (!hash || hash.length < 2) return;
    const id = decodeURIComponent(hash.slice(1));
    // Léger délai pour laisser le DOM se rendre après navigation client
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 60);
    return () => clearTimeout(t);
  }, [location.hash, normalizedPath]);

  // Recherche : d'abord dans les pages mergées (statiques + serveur),
  // puis fallback sur les pages statiques pour un rendu immédiat sans flash blanc.
  const page = useMemo(() => {
    const normalized = normalizedPath.replace(/\/+$/, '');
    const fromMerged = mergedPages.find((p) => p?.path === normalized);
    if (fromMerged) return fromMerged;
    return seoPages.find((p) => p.path === normalized) ?? null;
  }, [normalizedPath, mergedPages]);

  // CRITICAL: Tous les hooks doivent être appelés AVANT les early returns,
  // sinon React plante avec "Rendered more hooks than during the previous render".
  const siloChildren = useMemo(() => {
    if (!page) return [];
    const safe = (mergedPages ?? []).filter((p): p is NonNullable<typeof p> => Boolean(p));
    // On exclut les pages désactivées (isActive === false) afin qu'elles
    // ne s'affichent ni dans la grille "Approfondir le guide" (pages piliers),
    // ni dans le bloc "Articles connexes" (pages enfants).
    const visible = safe.filter((p) => p?.isActive !== false);
    if (page.isPillar) return visible.filter((p) => p?.parent === page.path);
    if (!page.parent) return [];
    return visible.filter((p) => p?.parent === page.parent && p?.id !== page.id);
  }, [page, mergedPages]);

  const pillarPage = useMemo(
    () => page?.isPillar ? page : (mergedPages ?? []).find((p) => p?.path === page?.parent) ?? null,
    [page, mergedPages]
  );

  // Pages SEO emploi spécifiques où l'on affiche les 3 dernières offres avant le contenu
  const isJobsHighlightPage =
  page?.path === "/maitre-nageur-sauveteur/emploi" ||
  page?.path === "/bnssa/emploi-bnssa";

  // Précalcul des offres DB approuvées pour le silo courant (utilisé pour
  // déclencher le batch-fetch des company_profiles afin d'enrichir le logo recruteur).
  const approvedDbSiloJobs = useMemo<RecruiterJob[]>(() => {
    if (!page || !isJobsHighlightPage) return [];
    return (recruiterJobs || []).filter(
      (rj) => rj.validationStatus === 'approved' && rj.category === page.silo
    );
  }, [page, isJobsHighlightPage, recruiterJobs]);

  // Batch-fetch des company_profiles des créateurs des offres approuvées du silo
  // courant pour récupérer leur logoUrl et brander les annonces du bloc
  // "Les 3 dernières offres" sur /bnssa/emploi-bnssa et /maitre-nageur-sauveteur/emploi.
  useEffect(() => {
    if (!isJobsHighlightPage) return;
    if (approvedDbSiloJobs.length === 0) return;
    const creators = Array.from(
      new Set(
        approvedDbSiloJobs
          .map((rj) => rj.creator)
          .filter((c): c is string => typeof c === 'string' && !!c)
      )
    );
    if (creators.length === 0) return;
    let cancelled = false;
    fetchCompanyProfilesByCreators(creators)
      .then((map) => {
        if (cancelled) return;
        setLogosByCreator(map);
      })
      .catch((e) => {
        console.error('[SeoPage] fetchCompanyProfilesByCreators failed', e);
      });
    return () => {
      cancelled = true;
    };
  }, [isJobsHighlightPage, approvedDbSiloJobs]);

  // Fusion offres statiques + offres recruteurs validées, filtrées par silo,
  // triées par date (DB d'abord) puis limitées aux 3 plus récentes.
  // CRITICAL: ce useMemo doit être appelé AVANT tout early return (règle des Hooks).
  const latestSiloJobs = useMemo(() => {
    if (!page || !isJobsHighlightPage) return [];
    // Enrichissement du logo recruteur via la jointure company_profiles
    const approvedDbJobs = approvedDbSiloJobs.map((rj) =>
      recruiterJobToJob({
        ...rj,
        logoUrl: rj.logoUrl || logosByCreator[rj.creator]?.logoUrl,
      })
    );
    const staticSiloJobs = jobs.filter((j) => j.category === page.silo);
    // Dédoublonnage par id (DB prioritaires)
    const seen = new Set<string>();
    const merged = [...approvedDbJobs, ...staticSiloJobs].filter((j) => {
      if (seen.has(j.id)) return false;
      seen.add(j.id);
      return true;
    });
    // Tri par publishedAt décroissant
    merged.sort((a, b) => (b.publishedAt || '').localeCompare(a.publishedAt || ''));
    return merged.slice(0, 3);
  }, [page, isJobsHighlightPage, approvedDbSiloJobs, logosByCreator]);

  // CRITICAL FIX MOBILE: tant que `hasFetchedSuccessfully` est faux ET qu'on
  // n'a pas trouvé la page dans les statiques, on AFFICHE LE LOADER (jamais 404).
  // Cela garantit qu'au refresh F5 sur une page custom mobile, on attend
  // patiemment la réponse Lumi (avec retry automatique) au lieu de tomber
  // immédiatement sur "Page introuvable".
  if (!page && !hasFetchedSuccessfully && !error) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block w-8 h-8 border-4 border-gray-200 border-t-red-600 rounded-full animate-spin mb-4" />
            <p className="text-gray-500 text-sm">Chargement de la page…</p>
          </div>
        </div>
      </Layout>);

  }

  // Cas spécial: erreur réseau ET aucune page trouvée → afficher un message
  // d'erreur explicite avec bouton de retry plutôt que 404.
  if (!page && error) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Connexion instable</h1>
            <p className="text-gray-600 mb-6 text-sm">
              Impossible de charger cette page. Vérifiez votre connexion internet et réessayez.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-colors">

              Recharger la page
            </button>
          </div>
        </div>
      </Layout>);

  }

  // Pas trouvée après chargement complet → afficher un message 404 plutôt que rediriger.
  // De même, une page désactivée (isActive === false) doit être masquée du site public
  // et renvoyer une 404 pour les visiteurs et les moteurs de recherche.
  if (!page || page.isActive === false) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Page introuvable</h1>
            <p className="text-gray-600 mb-6">
              La page demandée n'existe pas ou a été supprimée.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-colors">

              <ArrowLeft className="w-4 h-4" />
              Retour à l'accueil
            </Link>
          </div>
        </div>
      </Layout>);

  }

  const isBNSSA = page.silo === "BNSSA";
  const assets = isBNSSA ? BNSSA_ASSETS : MNS_ASSETS;
  const accentBg = isBNSSA ? "bg-red-600" : "bg-blue-600";
  const accentBgLight = isBNSSA ? "bg-red-50" : "bg-blue-50";
  const accentText = isBNSSA ? "text-red-700" : "text-blue-700";
  const accentBorder = isBNSSA ? "border-red-200" : "border-blue-200";
  const heroFrom = isBNSSA ? "from-red-950/90 via-red-900/50" : "from-blue-950/90 via-blue-900/50";
  const ctaBg = isBNSSA ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700";

  const safeSections = Array.isArray(page.sections) ? page.sections : [];
  const safeIntro = page.intro ?? '';
  const safeH1 = page.h1 ?? page.title ?? '';
  const relatedJobs = jobs.filter((j) => j.category === page.silo).slice(0, 3);
  const canonicalUrl = `https://bnssa-mns.fr${page.path}`;

  // Stats bar retirée à la demande client.

  // ─── JSON-LD FAQPage (page mère MNS uniquement) ─────────────────────────
  // On détecte la section FAQ (dernière section dont le titre commence par "FAQ")
  // sur la page /maitre-nageur-sauveteur et on extrait les couples Question/Réponse
  // à partir de la structure <h3>question</h3><p>réponse</p>… afin de générer
  // un schéma FAQPage conforme à schema.org/Google Rich Results.
  const faqJsonLd = useMemo<Record<string, unknown> | null>(() => {
    if (!page) return null;
    if (page.path !== "/maitre-nageur-sauveteur") return null;
    if (!Array.isArray(page.sections) || page.sections.length === 0) return null;

    // On cherche la section dont le titre démarre par "FAQ" (insensible à la casse,
    // entités HTML décodées au cas où le titre contiendrait des &nbsp;).
    const decodeEntities = (s: string): string =>
      s.
      replace(/&nbsp;/gi, ' ').
      replace(/&amp;/gi, '&').
      replace(/&quot;/gi, '"').
      replace(/&#39;/gi, "'").
      replace(/&apos;/gi, "'").
      replace(/&laquo;/gi, '«').
      replace(/&raquo;/gi, '»').
      replace(/&lt;/gi, '<').
      replace(/&gt;/gi, '>').
      replace(/&hellip;/gi, '…').
      replace(/&mdash;/gi, '—').
      replace(/&ndash;/gi, '–');

    const stripHtml = (s: string): string =>
      decodeEntities(s.replace(/<[^>]+>/g, '')).replace(/\s+/g, ' ').trim();

    const faqSection = page.sections.find((s) => {
      const t = stripHtml(s?.title || '').toLowerCase();
      return t.startsWith('faq');
    });
    if (!faqSection || !faqSection.content) return null;

    // Extraction des couples <h3>question</h3> + HTML jusqu'au prochain <h3>
    // (ou fin de chaîne) comme réponse.
    const html = faqSection.content;
    const regex = /<h3[^>]*>([\s\S]*?)<\/h3>([\s\S]*?)(?=<h3[^>]*>|$)/gi;
    const qa: { q: string; a: string }[] = [];
    let m: RegExpExecArray | null;
    while ((m = regex.exec(html)) !== null) {
      const q = stripHtml(m[1] || '');
      // Pour la réponse, on garde un texte propre (sans balises) afin d'éviter
      // que des balises mal fermées polluent le JSON-LD.
      const a = stripHtml(m[2] || '');
      if (q && a) qa.push({ q, a });
    }
    if (qa.length === 0) return null;

    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": qa.map(({ q, a }) => ({
        "@type": "Question",
        "name": q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": a
        }
      }))
    };
  }, [page]);

  // Gallery items
  const galleryPhotos = isBNSSA ?
  [
  { src: assets.hero, alt: "Sauveteur BNSSA en poste sur la plage", label: "Sur le terrain" },
  { src: assets.action, alt: "Exercice de sauvetage aquatique sur la plage", label: "Formation & exercices" },
  { src: assets.profile, alt: "Sauveteuse BNSSA en surveillance au bord du bassin", label: "Surveillance - Prévention" },
  { src: assets.aerial, alt: "Vue aérienne plage surveillance aquatique", label: "Vue d'ensemble" }] :

  [
  { src: assets.hero, alt: "Maître nageur en piscine olympique", label: "En piscine" },
  { src: assets.action, alt: "Cours de natation avec instructeur MNS", label: "Cours de natation" },
  { src: assets.profile, alt: "Portrait maître nageur professionnel", label: "Profil MNS" },
  { src: (assets as typeof MNS_ASSETS).pool, alt: "Piscine olympique centre aquatique", label: "Centre aquatique" }];


  return (
    <Layout>
      <Seo
        title={page.title}
        description={page.metaDescription}
        canonical={canonicalUrl}
        jsonLd={faqJsonLd ?? undefined} />


      {/* ─── BREADCRUMB ──────────────────────────────────────────────────── */}
      <nav aria-label="Fil d'Ariane" className="bg-white border-b border-gray-200">
        <ol className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center flex-wrap gap-1.5 text-sm">
          <li>
            <Link to="/" className="text-gray-500 hover:text-gray-900 transition-colors">
              Accueil
            </Link>
          </li>
          <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
          {page.isPillar ?
          <li className="font-semibold text-gray-900" aria-current="page">
              {page.keyword}
            </li> :

          <>
              {pillarPage &&
            <>
                  <li>
                    <Link to={pillarPage.path} className={`${accentText} hover:underline font-medium capitalize`}>
                      {pillarPage.keyword}
                    </Link>
                  </li>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                </>
            }
              <li className="font-semibold text-gray-900 capitalize" aria-current="page">
                {page.keyword}
              </li>
            </>
          }
        </ol>
      </nav>

      {/* ─── HERO PHOTO ──────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="relative min-h-[380px] sm:min-h-[440px] flex flex-col justify-end">
          {/* Background photo */}
          <img
            src={page.heroImage || assets.hero}
            alt={isBNSSA ? "Sauveteur BNSSA en poste sur la plage" : "Maître nageur sauveteur en piscine"}
            className="absolute inset-0 w-full h-full object-cover" />

          {/* Overlay */}
          <div className={`absolute inset-0 bg-gradient-to-t ${heroFrom} to-transparent`} />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />

          {/* Content */}
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 sm:pb-12 pt-8 sm:pt-20 w-full">
            {!page.isPillar && pillarPage &&
            <Link
              to={pillarPage.path}
              className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white mb-6 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/20 transition-colors">

                <ArrowLeft className="w-3.5 h-3.5" />
                Retour au guide {pillarPage.keyword}
              </Link>
            }

            <div className="flex items-center gap-2 mb-5 flex-wrap">
              <span className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 ${accentBg} text-white text-xs font-bold rounded-full`}>
                {isBNSSA ? <ShieldCheck className="w-3.5 h-3.5" /> : <Waves className="w-3.5 h-3.5" />}
                {page.silo}
              </span>
              {page.isPillar &&
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-xs font-bold rounded-full">
                  <Layers className="w-3.5 h-3.5" />
                  Guide complet
                </span>
              }
            </div>

            <h1
              className="text-2xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.1] sm:leading-[1.04] tracking-[-0.03em] mb-3 sm:mb-5 max-w-3xl"
              style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif" }}>

              {safeH1}
            </h1>
            <SeoIntroParagraph html={safeIntro} />

            {/* Hero CTAs */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mt-8">
              <Link
                to={isBNSSA ? "/bnssa/offres-emploi" : "/maitre-nageur-sauveteur/offres-emploi"}
                className={`inline-flex items-center justify-center gap-2 ${ctaBg} text-white px-6 py-3 rounded-xl font-bold text-sm transition-colors w-full sm:w-auto`}>

                <Briefcase className="w-4 h-4" />
                Voir les offres {page.silo}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to={isBNSSA ? "/bnssa/formations" : "/maitre-nageur-sauveteur/formations"}
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm border border-white/25 text-white px-6 py-3 rounded-xl font-bold text-sm hover:bg-white/20 transition-all w-full sm:w-auto">

                <GraduationCap className="w-4 h-4" />
                Formations {page.silo}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── STATS BAR (retirée) ─────────────────────────────────────────── */}


      {/* ─── 3 DERNIÈRES OFFRES (pages emploi BNSSA / MNS) ─────────────── */}
      {isJobsHighlightPage && latestSiloJobs.length > 0 &&
      <section className={`py-12 sm:py-14 ${accentBgLight} border-b ${accentBorder}`}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
              <div>
                <span className={`inline-block text-[11px] font-bold uppercase tracking-[0.14em] ${accentText} mb-2`}>
                  Tendances emploi
                </span>
                <h2
                className="text-2xl sm:text-3xl font-black text-gray-900 tracking-[-0.02em]"
                style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif" }}>
                  Les 3 dernières offres {page.silo}
                </h2>
              </div>
              <Link
              to={isBNSSA ? "/bnssa/offres-emploi" : "/maitre-nageur-sauveteur/offres-emploi"}
              className={`inline-flex items-center gap-2 ${ctaBg} text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-colors`}>
                Voir toutes les offres
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {latestSiloJobs.map((j) =>
            <JobCard key={j.id} job={j} />
            )}
            </div>
          </div>
        </section>
      }

      {/* ─── CONTENT + SIDEBAR ───────────────────────────────────────────── */}
      <section className="py-14 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Main content */}
            <div className="lg:col-span-2">
              {/* ─── Navigation rapide par ancres (pages piliers) ─────────── */}
              {page.isPillar && safeSections.length >= 2 && (() => {
                const anchors = safeSections.map((section, i) => {
                  const titleText = stripSectionTitle(section.title || '');
                  const slug = slugifyAnchor(titleText) || `section-${i + 1}`;
                  return {
                    id: `s-${i + 1}-${slug}`.slice(0, 80),
                    label: shortenAnchorLabel(titleText, i + 1),
                    full: titleText
                  };
                });
                return (
                  <nav
                    aria-label="Navigation rapide dans la page"
                    className={`mb-8 rounded-2xl border ${accentBorder} ${accentBgLight} p-4 sm:p-5`}>
                    <div className={`text-[11px] font-bold uppercase tracking-[0.14em] ${accentText} mb-3`}>
                      Navigation rapide
                    </div>
                    <ul className="flex flex-wrap gap-2">
                      {anchors.map((a, idx) => (
                        <li key={a.id}>
                          <a
                            href={`#${a.id}`}
                            title={a.full}
                            className={`group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${accentBorder} bg-white text-xs sm:text-sm font-semibold text-gray-800 hover:text-white hover:${accentBg.replace('bg-', 'bg-')} ${isBNSSA ? 'hover:bg-red-600 hover:border-red-600' : 'hover:bg-blue-600 hover:border-blue-600'} transition-colors`}>
                            <span className={`text-[10px] font-bold ${accentText} group-hover:text-white/90`}>
                              {String(idx + 1).padStart(2, '0')}
                            </span>
                            <span>{a.label}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </nav>);
              })()}

              <article className="prose prose-lg max-w-none">
                {safeSections.map((section, i) => {
                  // Compatibilité descendante : si le contenu ne contient pas de balise HTML,
                  // on l'enveloppe automatiquement dans un <p> pour préserver le rendu existant.
                  const raw = section.content || '';
                  const isHtml = /<\/?[a-z][\s\S]*>/i.test(raw);
                  const html = isHtml ?
                  raw :
                  `<p>${raw.replace(/\n\s*\n/g, '</p><p>').replace(/\n/g, '<br/>')}</p>`;
                  const titleText = stripSectionTitle(section.title || '');
                  const slug = slugifyAnchor(titleText) || `section-${i + 1}`;
                  const anchorId = `s-${i + 1}-${slug}`.slice(0, 80);
                  return (
                    <div key={i} className="mb-10">
                      <h2
                        id={anchorId}
                        className="scroll-mt-24 text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className={`w-1 h-7 ${accentBg} rounded-full inline-block flex-shrink-0`} />
                        {section.title}
                      </h2>
                      <div
                        className={`seo-rich-content text-gray-700 leading-relaxed text-base ${isBNSSA ? 'seo-accent-red' : 'seo-accent-blue'}`}
                        dangerouslySetInnerHTML={{ __html: html }} />

                    </div>);

                })}
              </article>

              {/* Pillar: child page grid */}
              {page.isPillar && siloChildren.length > 0 &&
              <div className="mt-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <Layers className={`w-6 h-6 ${accentText}`} />
                    Approfondir le guide {page.silo}
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Thématiques détaillées pour explorer toutes les facettes du {page.keyword}.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {siloChildren.map((p) =>
                  <Link
                    key={p.id}
                    to={p.path}
                    className={`group bg-white border ${accentBorder} rounded-xl p-5 hover:shadow-lg transition-shadow`}>

                        <div className="flex items-start justify-between gap-3 mb-2">
                          <span className={`text-xs font-bold uppercase tracking-wider ${accentText} capitalize`}>
                            {p.keyword}
                          </span>
                          <ArrowRight className={`w-4 h-4 ${accentText} group-hover:translate-x-1 transition-transform flex-shrink-0`} />
                        </div>
                        <h3 className="font-bold text-gray-900 leading-snug mb-2 group-hover:underline">
                          {p.h1}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {(p.intro ?? '')
                            .replace(/<[^>]+>/g, ' ')
                            .replace(/&nbsp;/gi, ' ')
                            .replace(/&amp;/gi, '&')
                            .replace(/&quot;/gi, '"')
                            .replace(/&#39;/gi, "'")
                            .replace(/&apos;/gi, "'")
                            .replace(/&laquo;/gi, '«')
                            .replace(/&raquo;/gi, '»')
                            .replace(/&lt;/gi, '<')
                            .replace(/&gt;/gi, '>')
                            .replace(/&hellip;/gi, '…')
                            .replace(/&mdash;/gi, '—')
                            .replace(/&ndash;/gi, '–')
                            .replace(/\s+/g, ' ')
                            .trim()}
                        </p>
                      </Link>
                  )}
                  </div>
                </div>
              }
            </div>

            {/* Sidebar */}
            <aside className="space-y-6">
              {/* Quick links */}
              <div className={`${accentBgLight} border ${accentBorder} rounded-2xl p-5`}>
                <h3 className={`font-bold ${accentText} mb-4 text-sm uppercase tracking-wider`}>
                  Accès rapides
                </h3>
                <div className="space-y-2">
                  {[
                  { to: isBNSSA ? "/bnssa/offres-emploi" : "/maitre-nageur-sauveteur/offres-emploi", label: `Offres d'emploi ${page.silo}`, icon: <Briefcase className="w-4 h-4" /> },
                  { to: isBNSSA ? "/bnssa/formations" : "/maitre-nageur-sauveteur/formations", label: `Formations ${page.silo}`, icon: <GraduationCap className="w-4 h-4" /> },
                  { to: "/inscription", label: "Créer mon compte", icon: <Award className="w-4 h-4" /> },
                  { to: "/backoffice", label: "Déposer une offre", icon: <Layers className="w-4 h-4" /> }].
                  map((link) =>
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`flex items-center gap-2.5 text-sm font-medium ${accentText} hover:underline py-1`}>

                      {link.icon}
                      {link.label}
                      <ArrowRight className="w-3.5 h-3.5 ml-auto opacity-50" />
                    </Link>
                  )}
                </div>
              </div>

              {/* Sidebar photo */}
              <div className="rounded-2xl overflow-hidden aspect-[4/3] relative">
                <img
                  src={assets.action}
                  alt={isBNSSA ? "Exercice de sauvetage aquatique" : "Cours de natation MNS"}
                  className="w-full h-full object-cover"
                  loading="lazy" />

                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 ${accentBg} text-white text-[10px] font-bold rounded-full`}>
                    {isBNSSA ? "Formation BNSSA" : "Formation MNS"}
                  </span>
                </div>
              </div>

              {/* Related jobs */}
              {relatedJobs.length > 0 &&
              <div className="bg-white border border-gray-100 rounded-2xl p-5" style={{ boxShadow: "rgba(0,0,0,0.02) 0px 0px 0px 1px, rgba(0,0,0,0.04) 0px 2px 8px" }}>
                  <h3 className="font-bold text-gray-900 mb-4 text-sm flex items-center gap-2">
                    <Briefcase className={`w-4 h-4 ${accentText}`} />
                    Offres en ce moment
                  </h3>
                  <div className="space-y-3">
                    {relatedJobs.map((j) =>
                  <Link
                    key={j.id}
                    to={`/offre/${j.id}`}
                    className="flex items-start justify-between gap-2 hover:opacity-80 transition-opacity group">

                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate group-hover:underline">{j.title}</div>
                          <div className="text-[11px] text-gray-500 truncate">{j.employer} · {j.location}</div>
                        </div>
                        <ArrowRight className={`w-3.5 h-3.5 ${accentText} flex-shrink-0 mt-0.5`} />
                      </Link>
                  )}
                  </div>
                  <Link
                  to={`/offres?categorie=${page.silo}`}
                  className={`mt-5 flex items-center justify-center gap-2 ${accentBg} hover:opacity-90 text-white px-4 py-2.5 rounded-xl font-semibold text-sm transition-opacity`}>

                    Toutes les offres {page.silo}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              }
            </aside>
          </div>
        </div>
      </section>

      {/* ─── PHOTO GALLERY (pillar pages only) ───────────────────────────── */}
      {page.isPillar &&
      <section className={`py-16 ${accentBgLight} border-t ${accentBorder}`}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <span className={`inline-block text-[11px] font-bold uppercase tracking-[0.14em] ${accentText} mb-2`}>
                  En images
                </span>
                <h2
                className="text-2xl font-black text-gray-900 tracking-[-0.02em]"
                style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif" }}>

                  Le métier {page.silo} en action
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {galleryPhotos.map((photo) =>
            <div
              key={photo.alt}
              className="group relative rounded-2xl overflow-hidden aspect-[4/3] bg-gray-100"
              style={{ boxShadow: "rgba(0,0,0,0.04) 0px 2px 8px" }}>

                  <img
                src={photo.src}
                alt={photo.alt}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out" />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent"></div>
                  <div className="absolute bottom-2.5 left-2.5 right-2.5">
                    <span className={`inline-block px-2.5 py-1 ${accentBg} text-white text-[10px] font-bold rounded-full`}>
                      {photo.label}
                    </span>
                  </div>
                </div>
            )}
            </div>
          </div>
        </section>
      }

      {/* ─── INTERNAL LINKING (child pages) ──────────────────────────────── */}
      {!page.isPillar && siloChildren.length > 0 &&
      <section className="py-14 bg-gray-50 border-t border-gray-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <BookOpen className={`w-5 h-5 ${accentText}`} />
              Articles connexes
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {siloChildren.map((p) =>
            <Link
              key={p.id}
              to={p.path}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow group flex items-center justify-between gap-3">

                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-500 mb-0.5 capitalize">{p.keyword}</div>
                    <div className="font-semibold text-gray-900 text-sm truncate">{p.h1}</div>
                  </div>
                  <ArrowRight className={`w-4 h-4 ${accentText} group-hover:translate-x-1 transition-transform flex-shrink-0`} />
                </Link>
            )}
              {pillarPage &&
            <Link
              to={pillarPage.path}
              className={`${accentBgLight} border ${accentBorder} rounded-lg p-4 hover:shadow-md transition-shadow group flex items-center justify-between gap-3`}>

                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-500 mb-0.5 capitalize">Page mère</div>
                    <div className="font-semibold text-gray-900 text-sm truncate">{pillarPage.h1}</div>
                  </div>
                  <Layers className={`w-4 h-4 ${accentText} flex-shrink-0`} />
                </Link>
            }
            </div>
          </div>
        </section>
      }
    </Layout>);

}

// ─── Helpers ancres pour la navigation rapide (pages piliers BNSSA / MNS) ───
// On strippe le HTML éventuel et on décode les principales entités pour obtenir
// un titre brut exploitable.
function stripSectionTitle(html: string): string {
  return html.
  replace(/<[^>]+>/g, ' ').
  replace(/&nbsp;/gi, ' ').
  replace(/&amp;/gi, '&').
  replace(/&quot;/gi, '"').
  replace(/&#39;/gi, "'").
  replace(/&apos;/gi, "'").
  replace(/&laquo;/gi, '«').
  replace(/&raquo;/gi, '»').
  replace(/&lt;/gi, '<').
  replace(/&gt;/gi, '>').
  replace(/&hellip;/gi, '…').
  replace(/&mdash;/gi, '—').
  replace(/&ndash;/gi, '–').
  replace(/\s+/g, ' ').
  trim();
}

// Convertit un titre en slug ASCII compatible avec un id d'ancre HTML.
function slugifyAnchor(text: string): string {
  return text.
  toLowerCase().
  normalize('NFD').
  replace(/[\u0300-\u036f]/g, '').
  replace(/[^a-z0-9]+/g, '-').
  replace(/^-+|-+$/g, '').
  slice(0, 50);
}

// ─── Mapping manuel des libellés d'ancres pour les pages piliers ──────────
// Pour chaque H2 connu des pages /bnssa et /maitre-nageur-sauveteur,
// on fixe un mot-clé SEO court (1 à 3 mots) en dur.
// La clé est le titre normalisé (minuscules, accents retirés, ponctuation
// supprimée) afin d'être robuste aux variations de casse, &nbsp;, etc.
const ANCHOR_LABEL_MAP: Record<string, string> = {
  // ─── Pilier BNSSA (/bnssa) ───────────────────────────────────────────
  "qu est ce que le bnssa et a quoi sert il en 2025": "BNSSA 2025",
  "les missions concretes du surveillant bnssa": "Missions",
  "formation bnssa programme duree et centres agrees": "Formation",
  "comment devenir bnssa le parcours etape par etape": "Devenir BNSSA",
  "prix et financement de la formation bnssa": "Prix & Financement",
  "salaire bnssa combien gagne un surveillant aquatique": "Salaire",
  "emploi bnssa secteurs qui recrutent et types de contrats": "Emploi",
  "bnssa ou bpjeps aan lequel choisir pour votre projet": "BNSSA ou BPJEPS",
  "recyclage bnssa et obligations reglementaires": "Recyclage",

  // ─── Pilier MNS (/maitre-nageur-sauveteur) ───────────────────────────
  "qu est ce qu un maitre nageur sauveteur en 2025": "Métier MNS",
  "les missions concretes du maitre nageur sauveteur": "Missions",
  "formation bpjeps aan la voie royale pour devenir mns": "BPJEPS AAN",
  "comment devenir maitre nageur le parcours etape par etape": "Devenir MNS",
  "salaire d un maitre nageur sauveteur combien gagne t il vraiment": "Salaire",
  "emploi mns les secteurs qui recrutent en france": "Emploi MNS",
  "bnssa bpjeps aan ou licence staps quelle voie pour quel projet": "BNSSA BPJEPS STAPS",
  "caepmns et obligations reglementaires du maitre nageur sauveteur": "CAEPMNS",
  "faq maitre nageur sauveteur les questions les plus frequentes": "FAQ"
};

// Génère un libellé court et SEO pour les boutons d'ancres.
// Stratégie :
// 1. On essaie d'abord le mapping manuel ANCHOR_LABEL_MAP (clé normalisée).
// 2. Sinon, fallback algorithmique : nettoyage, découpe sur séparateur fort,
//    suppression des mots vides, sélection de 2–3 mots-clés porteurs,
//    sigles métier en MAJ, autres mots en Title Case.
function shortenAnchorLabel(fullTitle: string, fallbackIndex: number): string {
  if (!fullTitle) return `Section ${fallbackIndex}`;

  // Sigles métier à toujours mettre en majuscules dans le label final
  const ACRONYMS = new Set([
    'bnssa', 'mns', 'bpjeps', 'aan', 'beesan', 'caepmns', 'pse', 'pse1', 'pse2',
    'faq', 'cdd', 'cdi', 'ae', 'staps', 'fcb', 'rgpd', 'siret', 'urssaf',
    'sst', 'rcp', 'qcm']);

  // Mots vides étendus (articles, prépositions, conjonctions, auxiliaires courants)
  const STOP = new Set([
    'le', 'la', 'les', 'un', 'une', 'des', 'du', 'de', 'd', 'l',
    'au', 'aux', 'à', 'a', 'pour', 'sur', 'par', 'en', 'et', 'ou', 'avec',
    'tout', 'toute', 'tous', 'toutes', 'qui', 'que', 'quoi', 'quel', 'quelle',
    'comment', 'pourquoi', 'quand', 'où', 'mon', 'ma', 'mes', 'son', 'sa', 'ses',
    'ce', 'cet', 'cette', 'ces', 'sans', 'dans', 'est', 'être', 'sont',
    'votre', 'vos', 'notre', 'nos', 'leur', 'leurs', 'plus', 'aussi',
    'puis', 'donc', 'mais', 'car', 'vs', 'entre', 'face']);

  const norm = (s: string) => s.toLowerCase().replace(/['’]/g, '').replace(/[^a-zà-ÿ0-9]/gi, '');

  // 0. LOOKUP DIRECT — mapping manuel des H2 connus
  // On normalise le titre complet : minuscules, suppression des accents,
  // remplacement de tous les caractères non alphanumériques par des espaces.
  const lookupKey = fullTitle.
    toLowerCase().
    normalize('NFD').
    replace(/[\u0300-\u036f]/g, '').
    replace(/[^a-z0-9]+/g, ' ').
    trim();
  if (ANCHOR_LABEL_MAP[lookupKey]) {
    return ANCHOR_LABEL_MAP[lookupKey];
  }

  // ─── Fallback algorithmique pour les sections non mappées ────────────
  let t = fullTitle.trim();

  // 1. Nettoyage initial : numérotation et ponctuation finale (« ? », « ! », « . »)
  t = t.replace(/^\s*(?:\d+|[IVX]+)\s*[\.\)\-–—:]\s*/i, '');
  t = t.replace(/[?!.\s]+$/g, '');

  // 2. Découpe sur séparateur fort : on garde le côté avec le plus de mots-clés métier
  const parts = t.split(/\s+[—–\-\|:]\s+/).map((p) => p.trim()).filter(Boolean);
  if (parts.length > 1) {
    const score = (s: string) => {
      const ws = s.split(/\s+/);
      let n = 0;
      for (const w of ws) {
        const k = norm(w);
        if (ACRONYMS.has(k)) n += 3;
        else if (k.length >= 6 && !STOP.has(k)) n += 2;
        else if (k.length >= 4 && !STOP.has(k)) n += 1;
      }
      return n;
    };
    parts.sort((a, b) => score(b) - score(a));
    t = parts[0];
  }

  // 3. Tokenisation + retrait des mots vides en tête et en queue
  let words = t.split(/\s+/).filter(Boolean);
  while (words.length > 1 && STOP.has(norm(words[0]))) words.shift();
  while (words.length > 1 && STOP.has(norm(words[words.length - 1]))) words.pop();

  // 4. Sélection des mots les plus porteurs SEO (3 max).
  const keep: string[] = [];
  for (const w of words) {
    const k = norm(w);
    if (!k) continue;
    if (keep.length > 0 && STOP.has(k)) continue;
    keep.push(w);
    if (keep.length >= 3) break;
  }

  // 5. Reformatage : sigles en MAJ, autres mots en Title Case
  const formatted = (keep.length > 0 ? keep : words.slice(0, 3)).map((w) => {
    const k = norm(w);
    if (ACRONYMS.has(k)) return w.toUpperCase();
    if (/^\d+$/.test(w)) return w;
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  });

  const out = formatted.join(' ').trim();
  return out.length > 0 ? out : `Section ${fallbackIndex}`;
}

// ─── Paragraphe d'intro SEO avec troncature mobile (Lire plus / Lire moins) ───
// Le HTML complet reste présent dans le DOM à tout moment (aucun `display:none`
// ni découpage côté serveur), garantissant que Google indexe 100% du texte.
// Sur mobile (< md), seul ~1/3 du texte est visible avec un fade en bas, et un
// petit bouton texte « Lire plus / Lire moins » permet d'étendre/replier.
// Sur desktop (md+), aucune troncature ni bouton.
function SeoIntroParagraph({ html }: {html: string;}) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Suivi du breakpoint md (768px) pour ne gérer le toggle qu'en mobile
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  // Détection du débordement réel pour n'afficher le bouton que si nécessaire
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const check = () => {
      // Quand collapsed, scrollHeight > clientHeight indique un débordement
      setOverflowing(el.scrollHeight - el.clientHeight > 4);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    window.addEventListener('resize', check);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', check);
    };
  }, [html, isMobile, expanded]);

  const isHtml = /<\/?[a-z][\s\S]*>/i.test(html);
  const collapsed = isMobile && !expanded;

  return (
    <div className="max-w-2xl">
      <div
        ref={ref}
        className={`relative seo-rich-content text-white/80 text-sm sm:text-base md:text-lg leading-snug md:leading-relaxed ${
        collapsed ? 'overflow-hidden line-clamp-2' : ''}`
        }>

        {isHtml ?
        <span dangerouslySetInnerHTML={{ __html: html }} /> :

        <span>{html}</span>
        }
        {collapsed && overflowing &&
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-black/70 to-transparent" />

        }
      </div>
      {isMobile && (overflowing || expanded) &&
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="md:hidden mt-2 text-xs font-semibold text-white/90 underline underline-offset-2 hover:text-white transition-colors"
        aria-expanded={expanded}>

          {expanded ? 'Lire moins' : 'Lire plus'}
        </button>
      }
    </div>);

}